import { useState, useEffect, useRef, useCallback } from 'react';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useCookies } from 'react-cookie';
import { db } from '../firebase';
import { SESSION_TIMEOUT } from 'shared';

const COOKIE_NAME = 'checkoutSession';
const STORAGE_KEY = 'ticketReservation';
const revive = session => session && ({
  ...session,
  date: new Date(session.date),
  createdAt: new Date(session.createdAt),
  expiresAt: new Date(session.expiresAt),
});
const secondsLeft = session => Math.max(0, Math.ceil((session?.expiresAt?.getTime() - Date.now()) / 1000) || 0);
const loadSaved = () => {
  try { return revive(JSON.parse(sessionStorage.getItem(STORAGE_KEY))); } catch { return undefined; }
};

export const useCheckoutSession = () => {
  const [currentSession, setCurrentSession] = useState(loadSaved);
  const sessionRef = useRef(currentSession);
  const [cookies, setCookie, removeCookie] = useCookies([COOKIE_NAME]);
  const initialCookie = useRef(cookies[COOKIE_NAME]);
  const [countDown, setCountDown] = useState(0);

  const remember = useCallback(session => {
    sessionRef.current = session;
    setCurrentSession(session);
    setCountDown(secondsLeft(session));
    // Embedded browsers can block cookies/storage. The active reservation lives in memory.
    try {
      if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch { /* Best-effort resume only. */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!sessionRef.current && initialCookie.current) {
      getDoc(doc(db, 'checkoutSession', initialCookie.current)).then(snapshot => {
        if (cancelled || sessionRef.current || !snapshot.exists()) return;
        const data = snapshot.data();
        remember(revive({ id: snapshot.id, ...data, date: data.date?.toDate(), createdAt: data.createdAt?.toDate(), expiresAt: data.expiresAt?.toDate() }));
      }).catch(() => { /* A failed resume must not break a new checkout. */ });
    }
    return () => { cancelled = true; };
  }, [remember]);

  useEffect(() => {
    const update = () => setCountDown(secondsLeft(sessionRef.current));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);

  const expireSession = async ({ forgetOnError = false } = {}) => {
    const session = sessionRef.current;
    const forget = () => {
      remember(undefined);
      try { removeCookie(COOKIE_NAME, { path: '/' }); } catch { /* Optional. */ }
    };
    // Successful payment must clear local recovery even if Firestore queues the
    // best-effort hold write offline instead of rejecting it immediately.
    if (forgetOnError) forget();
    try {
      if (session?.id) await updateDoc(doc(db, 'checkoutSession', session.id), { expiresAt: new Date() });
    } catch (error) {
      if (error.code !== 'not-found' && !forgetOnError) throw error;
    }
    if (!forgetOnError) forget();
  };

  const createCheckoutSession = async ({ location, visitDate, date, timeZone, timeSlot, groupSize }) => {
    await expireSession();
    const data = { location, date, timeSlot, groupSize, ...(visitDate && { visitDate }), ...(timeZone && { timeZone }), createdAt: new Date(), expiresAt: new Date(Date.now() + SESSION_TIMEOUT) };
    const ref = await addDoc(collection(db, 'checkoutSession'), data);
    const session = { ...data, id: ref.id };
    remember(session);
    try { setCookie(COOKIE_NAME, ref.id, { path: '/', maxAge: SESSION_TIMEOUT / 1000, sameSite: true }); } catch { /* Optional. */ }
    return session;
  };

  const renewSession = async () => {
    const session = sessionRef.current;
    if (!session?.id) throw new Error('Please choose a time to continue. Your details are saved.');
    const expiresAt = new Date(Date.now() + SESSION_TIMEOUT);
    await updateDoc(doc(db, 'checkoutSession', session.id), { expiresAt });
    const renewed = { ...session, expiresAt };
    remember(renewed);
    return renewed;
  };

  const rememberPaymentAttempt = paymentAttemptId => {
    if (sessionRef.current) remember({ ...sessionRef.current, paymentAttemptId });
  };

  return { countDown, currentSession, createCheckoutSession, expireSession, renewSession, rememberPaymentAttempt };
};
